<script setup lang="ts">
import TokenService from '@/services/token.service'
import type { DetectionModel } from '@/types/models/DetectionModel'
import {
    defaultSocketDetectionModel,
    type SocketCheckpointDetectionModel,
    type SocketDetectionModel,
    type SocketDeviceDetectionModel,
    type SocketEventDetectionModel
} from '@/types/socket/SocketDetectionModel'
import { io } from 'socket.io-client'
import { computed, inject, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import store from '../store'
import { EWebSocketPerm } from '../types/permissions/WebSocketPermissionsEnum'
import { SOCKET_RETRY_DELAY, buildDetectionArray, buildSocketStateArray } from './socketUtils'

const permissions = computed(() => store.state.authentication?.me?.permissions ?? [])
const userHasDetectionsPermission = computed(
    () => permissions.value?.find(item => item === EWebSocketPerm.WEBSOCKET_ACCESS)
)

type Props = {
    device_ids?: number[]
    site_ids?: number[]
    poi_ids?: number[]
}
const props = defineProps<Props>()
type Emits = {
    (e: 'update', value: SocketDetectionModel, latestDetections: DetectionModel[]): void
}
const emit = defineEmits<Emits>()

const URTIME_DETECTIONS_URL = `${
    import.meta.env.VITE_SOCKET_URL ?? window.location.origin
}/edf-detections`

const socketState = ref<Props>()
const isAdmin = inject<boolean>('isAdmin', false)

const socketDetections = ref<SocketDetectionModel>({
    ...defaultSocketDetectionModel
})
const socket = userHasDetectionsPermission.value
    ? io(URTIME_DETECTIONS_URL, {
          autoConnect: false,
          retries: 5,
          path: '/ws/edf-detections',
          transports: ['websocket'],
          query: {
              ...socketState.value,
              token: TokenService.getLocalAccessToken(),
              ...(isAdmin ? { admin: true } : {})
          },
          auth: { token: TokenService.getLocalAccessToken() }
      }).on('message', (res: DetectionModel[] | string) => {
          if (typeof res === 'string' && res === 'Authentication error')
              setTimeout(() => socket?.disconnect().connect(), SOCKET_RETRY_DELAY)
          if (res.length && socketState.value?.site_ids)
              socketDetections.value.event_detections =
                  buildDetectionArray<SocketEventDetectionModel>(
                      res as DetectionModel[],
                      socketDetections.value.event_detections,
                      'site_id'
                  )
          if (res.length && socketState.value?.device_ids?.length)
              socketDetections.value.device_detections =
                  buildDetectionArray<SocketDeviceDetectionModel>(
                      res as DetectionModel[],
                      socketDetections.value.device_detections,
                      'device_id'
                  )
          if (res.length && socketState.value?.poi_ids?.length)
              socketDetections.value.checkpoint_detections =
                  buildDetectionArray<SocketCheckpointDetectionModel>(
                      res as DetectionModel[],
                      socketDetections.value.checkpoint_detections,
                      'poi_id'
                  )
          emit('update', socketDetections.value, res as DetectionModel[])
      })
    : undefined

const setupDetectionsArrays = (site_ids?: number[], poi_ids?: number[], device_ids?: number[]) => {
    if (device_ids?.length)
        //@ts-ignore
        socketDetections.value.device_detections =
            buildSocketStateArray<SocketDeviceDetectionModel>(
                device_ids,
                socketDetections.value.device_detections,
                'device_id'
            )
    if (poi_ids?.length)
        //@ts-ignore
        socketDetections.value.checkpoint_detections =
            buildSocketStateArray<SocketCheckpointDetectionModel>(
                poi_ids,
                socketDetections.value.checkpoint_detections,
                'poi_id'
            )
    if (site_ids?.length)
        //@ts-ignore
        socketDetections.value.event_detections = buildSocketStateArray<SocketEventDetectionModel>(
            site_ids,
            socketDetections.value.event_detections,
            'site_id'
        )
}

const setSocket = (device_ids?: number[], site_ids?: number[], poi_ids?: number[]) => {
    if (device_ids?.length)
        !device_ids?.every(
            item => socketState.value?.device_ids?.find(deviceId => deviceId === item)
        )
            ? updateSocketState('device_ids', device_ids)
            : null

    if (site_ids?.length)
        !site_ids.every(item => socketState.value?.site_ids?.find(siteId => siteId === item))
            ? updateSocketState('site_ids', site_ids)
            : null
    if (poi_ids?.length)
        !poi_ids.every(item => socketState.value?.poi_ids?.find(poiId => poiId === item))
            ? updateSocketState('poi_ids', poi_ids)
            : null
}

const reloadSocket = () => {
    if (socket?.connected) socket?.disconnect().connect()
    else socket?.connect()
}

const updateSocketState = (key: string, value: unknown) => {
    setupDetectionsArrays(props.site_ids, props.poi_ids, props.device_ids)
    socketState.value = { ...socketState.value, [key]: value }
    if (socket) socket.io.opts.query = { ...socket?.io.opts.query, [key]: value }
    reloadSocket()
}

onMounted(() => setSocket(props.device_ids, props.site_ids, props.poi_ids))
watch(
    () => props.device_ids,
    (newValue?: number[]) => setSocket(newValue)
)
watch(
    () => props.site_ids,
    (newValue?: number[]) => setSocket(undefined, newValue)
)
watch(
    () => props.poi_ids,
    (newValue?: number[]) => setSocket(undefined, undefined, newValue)
)

onBeforeUnmount(() => {
    socket?.disconnect()
})
</script>
<template><slot /></template>
